import { useCallback, useEffect, useMemo, useReducer } from 'react';
import { type JourneyManager } from './useJourney';
import { isMobileDevice, isPhone } from '../utils/platform';
import { useIsEmbedded } from './useIsEmbedded';
import { useCancelConfirm } from './useCancelConfirm';
import { useTranslation } from '../i18n/i18n';
import { useParentMessaging } from './useParentMessaging';
import { useRequestedData } from './useRequestedData';
import { type LayoutData } from './useLayoutContext';
import {
  type AppAction,
  type AppState,
  appReducer,
  getInitialState,
} from '../journey/appState';
import { redirectInit } from '../journey/redirectInit';
import { embeddedInit } from '../journey/embeddedInit';
import { errorCode } from '../utils/abortVariants';
import {
  useAnnounceEmbeddedMode,
  useAnnounceReadyToShow,
} from './useEmbeddedHost';

// -- Hook --

export type AppFlow = {
  state: AppState;
  isMobile: boolean;
  isEmbedded: boolean;
  /** A phone (not a tablet): embedded Ronan opens as a drawer. */
  isPhone: boolean;
  layoutData: LayoutData;
  handleCancel: () => Promise<void>;
  dispatch: React.Dispatch<AppAction>;
};

export function useAppFlow(journey: JourneyManager): AppFlow {
  const { locale } = useTranslation();
  const isEmbedded = useIsEmbedded();
  const isMobile = isMobileDevice();
  const phone = isPhone();
  const parentMessaging = useParentMessaging(isEmbedded);
  // After useParentMessaging: Ronan must already listen for ronan:init when
  // the loader reacts to the mode.
  useAnnounceEmbeddedMode(isEmbedded, phone ? 'drawer' : 'card');

  const [state, dispatch] = useReducer(appReducer, undefined, getInitialState);
  // The start result (and with it the appearance) is applied, or an error is
  // shown: the loader may show the card.
  useAnnounceReadyToShow(
    isEmbedded && ('session' in state || state.type === 'error'),
  );

  // Let the journey display a generic error page when it can't redirect back to the client.
  // There is no client to return to in that case, so the error page omits the "Leave" action.
  useEffect(() => {
    journey.setErrorHandler(() =>
      dispatch({
        action: 'INIT_ERROR',
        variant: 'serverError',
        canLeave: false,
      }),
    );
  }, [journey]);

  // Embedded Ronan tells the page when the flow ends; the journey posts
  // complete, cancel and its own errors.
  useEffect(() => {
    journey.setNotifyParent(parentMessaging.notify);
  }, [journey, parentMessaging.notify]);

  // Init-level errors end the flow too. A cancel was already posted.
  useEffect(() => {
    if (state.type === 'error' && state.variant !== 'cancelled') {
      parentMessaging.notify({
        type: 'ronan:error',
        error: errorCode(state.variant),
      });
    }
  }, [state, parentMessaging.notify]);

  const selectedToken = 'selectedToken' in state ? state.selectedToken : null;
  const { groups: requestedDataGroups, loading: requestedDataLoading } =
    useRequestedData(selectedToken, locale);
  const baseCancelConfirm = useCancelConfirm();

  // In embedded mode there is no navigation on cancel, so after the user
  // confirms we transition to the abort page ourselves.
  const handleCancel = useCallback(async () => {
    await baseCancelConfirm();
    if (isEmbedded && journey.isRedirecting()) {
      dispatch({ action: 'INIT_ERROR', variant: 'cancelled' });
    }
  }, [baseCancelConfirm, isEmbedded, journey]);

  useEffect(() => {
    return isEmbedded
      ? embeddedInit(dispatch, journey, parentMessaging)
      : redirectInit(dispatch, journey, isMobile);
  }, [
    isEmbedded,
    isMobile,
    journey,
    parentMessaging.initData,
    parentMessaging.handshakeError,
  ]);

  // Build layout context from current state
  const layoutData: LayoutData = useMemo(() => {
    if ('session' in state) {
      const { session } = state;
      const request = session.context.request;
      return {
        client: request.client,
        requestType: request.type,
        interactionConfig: session.interactionConfig,
        requestedDataGroups,
        requestedDataLoading,
        demo: session.interactionConfig.demo,
      };
    }
    return { requestedDataGroups, requestedDataLoading };
  }, [state, requestedDataGroups, requestedDataLoading]);

  return {
    state,
    isMobile,
    isEmbedded,
    isPhone: phone,
    layoutData,
    handleCancel,
    dispatch,
  };
}
